// Groups a product's fields into the product page's sections (#467), by the
// field's name, the same way the vault card's back picks its story, care and
// origin (#399 tracks letting brands choose). A field that matches nothing
// stays a row under "More details", in the brand's order.

export type SectionId = 'price' | 'care' | 'fit' | 'materials' | 'made' | 'story' | 'repairs' | 'details';

export interface PageField {
  name: string;
  value: string;
}

export interface GroupedFields {
  price?: PageField;
  care: PageField[];
  fit: PageField[];
  materials: PageField[];
  made: PageField[];
  story: PageField[];
  repairs: PageField[];
  details: PageField[];
}

// Order matters: "Repair & Returns" is repairs before anything else; "Care &
// Reproofing" is care; "Knitted In" is where it's made, not materials.
const RULES: [SectionId, RegExp][] = [
  ['price', /^(price|prix|rrp|retail price)$/i],
  ['care', /wash|care|laundry|aftercare|looking after|entretien/i],
  ['repairs', /repair|réparation/i],
  ['made', /knitted in|woven in|workshop|made|origin|provenance|fabriqué/i],
  ['materials', /fibre|fiber|lining|cloth|fabric|shell|knit|weight|weave|material|composition|matière/i],
  ['fit', /\bcut\b|sizing|\bfit\b|rise|coupe/i],
  ['story', /story|note|the piece|dye lot|season|wearing|histoire/i],
];

export const sectionOf = (name: string): SectionId => {
  const hit = RULES.find(([, rx]) => rx.test(name.trim()));
  return hit ? hit[0] : 'details';
};

export function groupFields(fields: PageField[]): GroupedFields {
  const out: GroupedFields = { care: [], fit: [], materials: [], made: [], story: [], repairs: [], details: [] };
  for (const f of fields) {
    const id = sectionOf(f.name);
    if (id === 'price') {
      if (!out.price) out.price = f;
      else out.details.push(f);
    } else out[id].push(f);
  }
  return out;
}

/** "- Wash at 30 °C\n- Dry flat" → ["Wash at 30 °C", "Dry flat"]; a paragraph stays one line. */
export const careLines = (value: string): string[] =>
  value
    .split(/\n+/)
    .map(l => l.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, '').replace(/\*\*/g, '').trim())
    .filter(Boolean);
